import { memo, useCallback } from 'react'

import { Badge } from '@mui/material'
import { AvcTaskStateEnum } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery } from '@tanstack/react-query'
import { pick } from 'lodash-es'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Header } from '@shared/components/Header'
import { Tabs } from '@shared/components/MaterialUIEnhancements/Tabs'
import { Content } from '@shared/layouts'
import { NavigationTabLink } from 'components/LinkedMUIComponents/NavigationTabLink'
import { StartStopButton } from 'components/StartStopButton'
import { Actions, DynamicBadge } from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask'
import { useAvcTaskStartMutation, useAvcTaskStopMutation, useAvcTaskUpdateMutation } from 'hooks/mutations/content'
import {
  avcTaskConfigRoute,
  avcTaskMonitoringRoute,
  avcTaskRawRoute,
  avcTaskRoute,
  avcTaskStatusRoute,
} from 'pages/NWPlatform/Workspaces/Workspace/AvcTasks/AvcTask'
import { avcTaskQueryKeys } from 'services/queryKeys'
import { RefetchIntervals } from 'types/query'
import { simplifyAvcTaskState } from 'utils'

const StructureRaw = () => {
  const { t } = useTranslation(['common', 'pages', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { avcTaskId } = avcTaskRoute.useParams()

  const avcTaskQuery = useQuery({
    ...avcTaskQueryKeys.detail({ taskId: avcTaskId }),
    refetchInterval: RefetchIntervals.NORMAL,
  })
  const { data: avcTask } = avcTaskQuery

  const { mutate, ...avcTaskUpdateMutation } = useAvcTaskUpdateMutation()
  const avcTaskStartMutation = useAvcTaskStartMutation()
  const avcTaskStopMutation = useAvcTaskStopMutation()

  const derivedState: AvcTaskStateEnum | undefined = avcTaskStartMutation.isPending
    ? 'STARTING'
    : avcTaskStopMutation.isPending
      ? 'STOPPING'
      : undefined

  const handleUpdateAvcTaskName = useCallback(
    (title: string, closeCallback: () => void) => {
      mutate(
        {
          taskId: avcTaskId,
          avcTaskEdit: { name: title },
        },
        {
          onError: () => enqueueSnackbar(t('pages:Workspace.updateError')),
          onSettled: closeCallback,
        },
      )
    },
    [t, mutate, avcTaskId, enqueueSnackbar],
  )
  return (
    <Content
      HeaderSlot={
        <Header
          title={avcTaskQuery.data?.name ?? ''}
          isLoading={avcTaskQuery.isLoading}
          isSaving={avcTaskUpdateMutation.isPending}
          onSave={handleUpdateAvcTaskName}
          appendix={
            avcTaskQuery.isSuccess && (
              <DynamicBadge {...pick(avcTaskQuery.data, 'activeVersionId', 'versionId', 'state', 'health')} />
            )
          }
          Actions={
            <>
              {avcTaskQuery.isSuccess && (
                <StartStopButton
                  state={simplifyAvcTaskState(derivedState ?? avcTaskQuery.data.state)}
                  onStart={() => avcTaskStartMutation.mutate({ taskId: avcTaskQuery.data.id })}
                  onStop={() => avcTaskStopMutation.mutate({ taskId: avcTaskQuery.data.id })}
                />
              )}
              <Actions />
            </>
          }
        />
      }
      SubNavigation={
        <Tabs>
          <NavigationTabLink from={avcTaskRoute.to} to={avcTaskConfigRoute.to}>
            {t('pages:Workspace.task')}
          </NavigationTabLink>
          <NavigationTabLink from={avcTaskRoute.to} to={avcTaskMonitoringRoute.to}>
            <Badge variant="dot" badgeContent={avcTask?.alerts.length} color="error">
              {t('pages:Workspace.monitoring')}
            </Badge>
          </NavigationTabLink>
          <NavigationTabLink from={avcTaskRoute.to} to={avcTaskStatusRoute.to}>
            {t('pages:Workspace.statusConfig')}
          </NavigationTabLink>
          <NavigationTabLink from={avcTaskRoute.to} to={avcTaskRawRoute.to}>
            {t('pages:Workspace.config')}
          </NavigationTabLink>
        </Tabs>
      }
    />
  )
}

export const Structure = memo(StructureRaw)
